import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { getSummarizableText, getSummaryScope, replaceWithSummary } from './utils';
import SummarizeButton from './SummarizeButton';
import NotesSummaryPopover from './NotesSummaryPopover';

const NotesSummarize = ({ editorRef, onReplaced }) => {
  const [summary, setSummary] = useState(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getQuill = useCallback(() => editorRef?.current?.getEditor?.(), [editorRef]);

  const close = useCallback(() => setSummary(null), []);

  const handleSummarize = useCallback(() => {
    const quill = getQuill();
    if (!quill) return;
    const range = quill.getSelection();
    const selection = range?.length > 0 ? range : null;
    setSummary({
      range: selection,
      scope: getSummaryScope(!!selection),
      text: getSummarizableText(quill, selection),
    });
  }, [getQuill]);

  const handleReplace = useCallback(async (bullets) => {
    const quill = getQuill();
    if (!quill) return false;
    replaceWithSummary({ quill, range: summary.range, bullets });
    close();
    return onReplaced(quill.root.innerHTML);
  }, [getQuill, summary, close, onReplaced]);

  useEffect(() => {
    const quill = getQuill();
    if (!quill) return undefined;

    const syncSelection = (range) => setHasSelection(range?.length > 0);
    const syncContent = () => setHasContent(quill.getLength() > 1);

    syncContent();
    quill.on('selection-change', syncSelection);
    quill.on('text-change', syncContent);
    return () => {
      quill.off('selection-change', syncSelection);
      quill.off('text-change', syncContent);
    };
  }, [getQuill]);

  return (
    <>
      <SummarizeButton
        scope={getSummaryScope(hasSelection)}
        disabled={!hasContent}
        onClick={handleSummarize}
      />
      {summary && (
        <NotesSummaryPopover
          scope={summary.scope}
          selectedText={summary.text}
          onReplace={handleReplace}
          onClose={close}
        />
      )}
    </>
  );
};

NotesSummarize.propTypes = {
  editorRef: PropTypes.shape({ current: PropTypes.any }),
  onReplaced: PropTypes.func.isRequired,
};

NotesSummarize.defaultProps = {
  editorRef: null,
};

export default React.memo(NotesSummarize);
